import React, { useEffect, useState, useRef } from "react";
import "./dataImport.css";
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";
import { IoMdClose } from 'react-icons/io';
import Papa from "papaparse";
import * as d3 from "d3";
import { FaTableCells } from "react-icons/fa6";
import { FaChartBar } from "react-icons/fa";
import Table from "./table";
import Charts from "./charts";
import {
    commitinternaldc,
    daintdcEditCommit,
    commitInternalSch,
    commitDAEnt,
    commitISGSDC,
    commitLoad
} from './api';

const DataImport = () => {
    const [openDropdown, setOpenDropdown] = useState(null);
    const [selectedOption, setSelectedOption] = useState("Load Data");
    const [selectedMapOption, setSelectedMapOption] = useState("");
    const [filteredMapOptions, setFilteredMapOptions] = useState([]);
    const dropdownRef = useRef(null);
    const getTodayInIST = () => {
        const now = new Date();

        const istOffset = 5.5 * 60 * 60 * 1000;
        const istDate = new Date(now.getTime() + istOffset);

        return istDate.toISOString().split("T")[0];
    };

    const getTomorrowInIST = () => {
        const now = new Date();

        const istOffset = 5.5 * 60 * 60 * 1000;
        const istDate = new Date(now.getTime() + istOffset);

        istDate.setDate(istDate.getDate() + 1);
        return istDate.toISOString().split("T")[0];
    };
    const [selectedDate, setSelectedDate] = useState(getTodayInIST());
    const [dragOver, setDragOver] = useState(false);
    const options = ["Load Data", "Market Data", "ISGS-Gen", "Internal-gen"];
    const [files, setFiles] = useState([]);
    const mapOptionsData = {
        "Load Data": ["Load Data"],
        "Market Data": ["Day-ahead"],
        "ISGS-Gen": ["Current Schedule", "Day-ahead Entitlement"],
        "Internal-gen": ["Current Schedule", "Day-ahead Entitlement"]
    };
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedFileIndex, setSelectedFileIndex] = useState(null);
    const [showTable, setShowTable] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const marketBarRef = useRef(null);
    const [fileUploaded, setFileUploaded] = useState(false);
    const [csvData, setCsvData] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [xData, setXData] = useState([]);
    const [yData, setYData] = useState([]);
    const tableRef = useRef(null);
    const chartRef = useRef(null);
    const BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const [statusMessage, setStatusMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setFilteredMapOptions(mapOptionsData[selectedOption] || []);
        setSelectedMapOption(mapOptionsData[selectedOption]?.[0] || "");
    }, [selectedOption]);

    useEffect(() => {
        if (statusMessage) {
            const timer = setTimeout(() => {
                setStatusMessage('');
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [statusMessage]);

    const handleSelect = (option, type) => {
        if (type === "market") {
            setSelectedOption(option);
        } else {
            setSelectedMapOption(option);
        }
        setOpenDropdown(null);
    };

    const toggleDropdown = (type) => {
        setOpenDropdown(openDropdown === type ? null : type);
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            marketBarRef.current?.requestFullscreen();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    useEffect(() => {
        const handleExitFullscreen = () => {
            if (!document.fullscreenElement) {
                setIsFullscreen(false);
            }
        };

        document.addEventListener("fullscreenchange", handleExitFullscreen);
        return () => {
            document.removeEventListener("fullscreenchange", handleExitFullscreen);
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleDragOver = (event) => {
        event.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = () => {
        setDragOver(false);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setDragOver(false);
        const droppedFiles = event.dataTransfer.files;
        handleFileUpload(droppedFiles);
    };

    const handleRemoveFile = (index) => {
        setFiles((prevFiles) => {
            const updatedFiles = prevFiles.filter((_, i) => i !== index);
            if (updatedFiles.length === 0) {
                setHeaders([]);
                setTableData([]);
                setCsvData([]);
                setXData([]);
                setYData([]);
                setFileUploaded(false);
            } else {
                processFiles(updatedFiles);
            }
            return updatedFiles;
        });
    };

    const handleFileUpload = (newFiles) => {
        const csvFiles = Array.from(newFiles).filter(file => file.name.endsWith('.csv') && file.size <= 15 * 1024 * 1024);
        const invalidFiles = Array.from(newFiles).filter(file => !file.name.endsWith('.csv'));
        const oversizedFiles = Array.from(newFiles).filter(file => file.size > 15 * 1024 * 1024);

        if (invalidFiles.length > 0) {
            setErrorMessage("Invalid file type. Please upload only .csv files.");
            setTimeout(() => setErrorMessage(""), 3000);
        }

        if (oversizedFiles.length > 0) {
            setErrorMessage("File size exceeds 15MB limit. Please upload files within the size limit.");
            setTimeout(() => setErrorMessage(""), 3000);
        }

        if (csvFiles.length > 0) {
            setFileUploaded(true);
            setFiles((prevFiles) => {
                const combinedFiles = [...prevFiles, ...csvFiles];
                processFiles(combinedFiles);
                return combinedFiles;
            });
        }
    };

    const processFiles = (filesArray) => {
        const allData = [];
        const allHeaders = new Set();

        filesArray.forEach((file, index) => {
            const reader = new FileReader();
            reader.onload = ({ target }) => {
                const parsedData = Papa.parse(target.result, { header: true, skipEmptyLines: true });

                parsedData.meta.fields.forEach(field => allHeaders.add(field));
                allData.push(...parsedData.data);

                if (index === filesArray.length - 1) {
                    const uniqueHeaders = Array.from(allHeaders);

                    const detectDataType = (values) => {
                        const isDate = (str) => {
                            const parts = str.split("-");
                            return (
                                parts.length === 3 &&
                                parts[0].length <= 2 &&
                                parts[1].length <= 2 &&
                                parts[2].length === 4
                            );
                        };

                        if (values.every(v => v !== "" && !isNaN(v))) return "number";
                        if (values.every(v => isDate(v))) return "date";
                        if (values.every(v => v === "true" || v === "false")) return "boolean";
                        return "string";
                    };

                    const headersWithMetadata = uniqueHeaders.map(header => {
                        const columnValues = allData.map(row => row[header]).filter(v => v !== undefined && v !== null);
                        return {
                            name: header,
                            dataType: detectDataType(columnValues),
                            editable: true,
                            width: 150,
                            displayName: header,
                        };
                    });

                    const dateHeaders = headersWithMetadata
                        .filter(h => h.dataType === "date")
                        .map(h => h.name);

                    const toYYYYMMDD = (dateStr) => {
                        if (!dateStr || typeof dateStr !== "string") return dateStr;

                        const parts = dateStr.split("-");
                        if (parts.length === 3) {
                            const [dd, mm, yyyy] = parts;
                            if (yyyy.length === 4) {
                                return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
                            }
                        }
                        return dateStr;
                    };

                    const normalizedData = allData.map(row => {
                        const normalizedRow = {};
                        uniqueHeaders.forEach(header => {
                            let value = row[header] || "";
                            if (dateHeaders.includes(header)) {
                                value = toYYYYMMDD(value);
                            }
                            normalizedRow[header] = value;
                        });
                        return normalizedRow;
                    });

                    setCsvData(normalizedData);
                    setHeaders(headersWithMetadata);
                    setTableData(normalizedData);
                    prepareChartData(normalizedData, uniqueHeaders);
                }
            };
            reader.readAsText(file);
        });
    };


    const prepareChartData = (data, fields) => {
        if (data.length > 0 && fields.length > 1) {
            const xValues = data.map((_, index) => index + 1);

            const numericFields = fields.filter((field) =>
                data.some((row) => !isNaN(parseFloat(row[field])) && isFinite(row[field]))
            );

            const yValues = numericFields.map((field, index) => ({
                field,
                values: data.map((row) => parseFloat(row[field]) || 0),
                color: d3.schemeCategory10[index % 10],
            }));

            setXData(xValues);
            setYData(yValues);
        }
    };

    const formatTableDataForAPI = (tableData, headers, fileName = "", selectedDate = "") => {
        const excludedHeaders = [
            "region", "state", "discom", "pool", "date", "buyer",
            "block_no", "rev_no", "Grand Total", "Total",
            "Time Block", "Time Desc", "Time"
        ];

        const normalizeHeader = (h) => h.toLowerCase().replace(/[\s_]/g, "");

        const unitNameHeaders = headers
            .map(header => header.name)
            .filter(header => !excludedHeaders.includes(header));

        const formattedData = [];

        let extractedDate = null;
        const datePatterns = [
            /(\d{2}-\d{2}-\d{4})$/,
            /(\d{4}-\d{2}-\d{2})$/,
            /(\d{2}-\d{2}-\d{4})/,
            /(\d{4}-\d{2}-\d{2})/,
        ];

        for (let pattern of datePatterns) {
            const match = fileName.match(pattern);
            if (match) {
                extractedDate = match[1];
                break;
            }
        }

        if (!extractedDate && selectedDate) {
            extractedDate = selectedDate;
        }

        const toYYYYMMDD = (dateStr) => {
            if (!dateStr || typeof dateStr !== "string") return null;

            const parts = dateStr.split("-");
            if (parts.length === 3) {
                if (parts[0].length === 4) return dateStr; // already YYYY-MM-DD
                return `${parts[2].padStart(2, '0')}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
            }
            return null;
        };

        tableData.forEach(row => {
            unitNameHeaders.forEach(unit_name => {
                if (row[unit_name] !== undefined && row[unit_name] !== "") {
                    const revNo = row.rev_no !== undefined ? parseFloat(row.rev_no) : 1;

                    const blockKey = Object.keys(row).find(key =>
                        normalizeHeader(key) === "blockno"
                    );

                    const blockNo = blockKey ? parseFloat(row[blockKey]) :
                        (row["Time Block"] ? parseFloat(row["Time Block"]) : null);
                    if (blockNo === null) return;

                    let formattedDate = row.date ? toYYYYMMDD(row.date) : toYYYYMMDD(extractedDate);
                    if (!formattedDate) {
                       // console.warn("Invalid or missing date for row:", row);
                        return;
                    }

                    formattedData.push([
                        revNo,
                        formattedDate,
                        blockNo,
                        unit_name,
                        parseFloat(row[unit_name])
                    ]);
                }
            });
        });

        return formattedData;
    };

    const formatDataForAPI = (tableData, headers) => {
        const formattedData = [];

        tableData.forEach(row => {
            if (!row.date || !row.block_no) {
             //   console.warn("Missing required fields in row:", row);
                return;
            }

            const formattedDate = row.date;

            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (!dateRegex.test(formattedDate)) {
                //console.warn("Invalid date format in row (expecting YYYY-MM-DD):", row);
                return;
            }

            const blockNo = parseInt(row.block_no, 10);
            if (isNaN(blockNo)) {
              //  console.warn("Invalid block number in row:", row);
                return;
            }

            const unitNameHeaders = headers
                .map(header => header.name)
                .filter(name => name !== "date" && name !== "block_no");

            let firstNumericValue = null;
            for (const header of unitNameHeaders) {
                if (!isNaN(row[header]) && row[header] !== "" && row[header] !== null) {
                    firstNumericValue = parseFloat(row[header]);
                    break;
                }
            }

            if (firstNumericValue === null) {
                //console.warn("No valid numeric column found in row:", row);
                return;
            }

            formattedData.push([formattedDate, blockNo, firstNumericValue]);
        });

        return formattedData;
    };


    const handleCommit = async () => {
         setLoading(true);
        try {
            const updatedTableData = tableRef.current?.getUpdatedData();
            if (!updatedTableData || updatedTableData.length === 0) {
                setStatusMessage("No updated table data available.");
                setMessageType('error');
                return;
            }

            let apiCalls = [];

            if (selectedOption === "Load Data") {
                const loadData = formatDataForAPI(updatedTableData, headers);
                if (loadData.length === 0) {
                    setStatusMessage("No valid Load Data to commit.");
                    setMessageType('error');
                    return;
                }
                // apiCalls = [commitLoad(loadData)];
            } else if (selectedOption === "Market Data") {
                const formattedData = formatTableDataForAPI(updatedTableData, headers);
                // formattedData.forEach(row => row.unshift("INTERNAL"));
                // apiCalls = [commitinternaldc(formattedData), daintdcEditCommit(formattedData)];
            } else if (selectedOption === "ISGS-Gen") {
                const formattedData = formatTableDataForAPI(updatedTableData, headers);
                const apiRequestBody = formatTableDataForAPI(tableData, headers);
                apiCalls = [commitISGSDC(apiRequestBody)];
                formattedData.forEach(row => row.unshift("ISGS"));
                apiCalls = [commitDAEnt(formattedData)];
            } else if (selectedOption === "Internal-gen") {
                const formattedData = formatTableDataForAPI(updatedTableData, headers);
                const apiRequestBody = formatTableDataForAPI(tableData, headers);
                apiCalls = [commitinternaldc(apiRequestBody)];
                formattedData.forEach(row => row.unshift("INTERNAL"));
                apiCalls = [commitDAEnt(formattedData)];
            } else {
                setStatusMessage("Invalid selectedOption for commit.");
                setMessageType('error');
                return;
            }

            const responses = await Promise.all(apiCalls);

            const hasError = responses.some(res => res.status !== 200);
            if (hasError) {
                setStatusMessage("Some API calls failed.");
                setMessageType('error');
            } else {
                setStatusMessage("Data committed successfully!");
                setMessageType('success');
            }
        } catch (error) {
            setStatusMessage("Commit API Call Failed.");
            setMessageType('error');
          //  console.error("Commit API Call Failed:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div ref={dropdownRef}>
            {loading && (
                <div className="dashboard-loading-bar"></div>
            )}
            <div className="top-bar">
                <div className="date-picker">
                    <input
                        id="date-input"
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        placeholder=" "
                    />
                    <label htmlFor="date-input">Date</label>
                </div>

                <div className="dropdown-container">
                    <button onClick={() => toggleDropdown("market")} className="dropdown-button">
                        {selectedOption} {openDropdown === "market" ? <IoMdArrowDropup /> : <IoMdArrowDropdown />}
                    </button>

                    {openDropdown === "market" && (
                        <div className="dropdown-menus">
                            {options.map((option, index) => (
                                <div
                                    key={index}
                                    onClick={() => handleSelect(option, "market")}
                                    className={`dropdown-item ${selectedOption === option ? "selected" : ""}`}
                                >
                                    {option}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* <div className="dropdown-container">
                    <button onClick={() => toggleDropdown("map")} className="dropdown-button">
                        {selectedMapOption || "Select Map Option"} {openDropdown === "map" ? <IoMdArrowDropup /> : <IoMdArrowDropdown />}
                    </button>

                    {openDropdown === "map" && (
                        <div className="dropdown-menus">
                            {filteredMapOptions.map((option, index) => (
                                <div
                                    key={index}
                                    onClick={() => handleSelect(option, "map")}
                                    className={`dropdown-item ${selectedMapOption === option ? "selected" : ""}`}
                                >
                                    {option}
                                </div>
                            ))}
                        </div>
                    )}
                </div> */}
            </div>
            {statusMessage && (
                <div className={`status-message ${messageType}`}>
                    {statusMessage}
                </div>
            )}
            <div className="dataImport-content-bar">
                {errorMessage && <div className="error-popup">{errorMessage}</div>}
                <div
                    className={`dataImport-dropzones ${dragOver ? 'drag-over' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <p>Drag and drop your files here</p>
                    <p className="limit-text">Limit 15MB per file. Supported files: .CSV</p>
                    <input
                        type="file"
                        accept=".csv"
                        multiple
                        onChange={(e) => handleFileUpload(e.target.files)}
                        className="file-input"
                    />
                    <button className="browse-button" onClick={() => document.querySelector('.file-input').click()}>
                        Browse Files
                    </button>

                    <ul className="file-list">
                        {files.map((file, index) => (
                            <li key={index} className="file-item" onClick={() => setSelectedFileIndex(index)}>
                                <span className="file-name">{file.name}</span>
                                <IoMdClose className="remove-icon" onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveFile(index);
                                }} />
                            </li>
                        ))}
                    </ul>

                    {files.length > 0 && <p className="file-count">{files.length} file(s) selected</p>}
                </div>
                {/* <div className="data-section">
                    {fileUploaded && (
                        <span
                            onClick={() => setShowTable(!showTable)}
                            style={{ cursor: "pointer", top: 10, right: 20 }}
                        >
                            {showTable ? <FaChartBar size={24} /> : <FaTableCells size={24} />}
                        </span>
                    )}


                    {showTable ? (
                        tableData.length > 0 && (
                            <Table ref={tableRef} headers={headers} rows={tableData} csvData={csvData} edit={true} sorting={true} filtering={true} exporting={true} addRow={false} />
                        )
                    ) : (
                        <Charts ref={chartRef} xData={xData} yData={yData} drag={true} edit={true} />
                    )}
                </div> */}

                <div className="data-section">

                    {showTable && tableData.length > 0 && (
                        <div className="data-section">
                            <Table
                                ref={tableRef}
                                headers={headers}
                                 maxHeight="550px"
                                rows={tableData}
                                csvData={csvData}
                                edit={true}
                                sorting={true}
                                drag={true}
                                filtering={true}
                                exporting={true}
                                addRow={false}
                                restrictEditToNewRows={false}
                            />
                        </div>
                    )}

                </div>
                <div className="dataImport-commit-container">
                    <div
                        className="dataImport-commit"
                        onClick={handleCommit}
                    >
                        COMMIT
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DataImport;
